import { useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Check, ChevronLeft, Loader2, Paperclip, X } from "lucide-react";
import { wizardOptions } from "@/data/content";
import { site } from "@/data/site";
import { track } from "@/lib/analytics";
import {
  buildSummary,
  submitCustomRequest,
  UPLOAD_LIMITS,
  type CustomRequest,
  type SubmitResult,
} from "@/lib/custom-request";
import { CtaButton } from "./cta";
import { cn } from "@/lib/utils";

const empty: CustomRequest = {
  interest: "",
  recipient: "",
  occasion: "",
  style: "",
  materials: [],
  personalization: [],
  personalizationDetail: "",
  completionDate: "",
  budget: "",
  quantity: "1",
  inspirationFiles: [],
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  contactMethod: "Email",
  notes: "",
};

const contactSchema = z.object({
  firstName: z.string().trim().min(1, "Please enter your first name.").max(60),
  lastName: z.string().trim().min(1, "Please enter your last name.").max(60),
  email: z.string().trim().min(1, "Please enter your email address.").email("Please enter a valid email address.").max(160),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  contactMethod: z.string().min(1),
  notes: z.string().trim().max(1200, "Please keep notes under 1200 characters.").optional().or(z.literal("")),
});

const stepTitles = [
  "What are you interested in?",
  "Who is it for?",
  "What is the occasion?",
  "What style do you prefer?",
  "Materials and colors",
  "Personalization",
  "Timing and budget",
  "Inspiration",
  "Your contact details",
  "Review and submit",
];

export function CustomRequestWizard({ reference }: { reference?: string }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState<CustomRequest>({ ...empty, reference });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [privacyOk, setPrivacyOk] = useState(false);
  const [copied, setCopied] = useState(false);
  const honeypot = useRef<HTMLInputElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const total = stepTitles.length;

  const set = <K extends keyof CustomRequest>(key: K, value: CustomRequest[K]) =>
    setData((d) => ({ ...d, [key]: value }));

  const toggleMulti = (key: "materials" | "personalization", value: string) =>
    setData((d) => ({
      ...d,
      [key]: d[key].includes(value) ? d[key].filter((v) => v !== value) : [...d[key], value],
    }));

  const focusHeading = () => requestAnimationFrame(() => headingRef.current?.focus());

  const goNext = () => {
    if (step === 0) track("begin_custom_request");
    const err = validate(step, data);
    setErrors(err);
    if (Object.keys(err).length) return;
    track("custom_request_step_complete", { step: step + 1 });
    setStep((s) => Math.min(s + 1, total - 1));
    focusHeading();
  };

  const goBack = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
    focusHeading();
  };

  const onSubmit = async () => {
    if (honeypot.current?.value) return; // silent spam drop
    if (!privacyOk) {
      setErrors({ privacy: "Please confirm you are happy for Paul to contact you about this request." });
      return;
    }
    setErrors({});
    setSubmitting(true);
    track("submit_custom_request");
    const res = await submitCustomRequest(data);
    setSubmitting(false);
    setResult(res);
    if (res.status === "error") track("custom_request_error");
    else track("custom_request_success", { delivery: res.status });
    focusHeading();
  };

  const summary = useMemo(() => buildSummary(data), [data]);

  if (result && result.status !== "error") {
    return (
      <div className="surface-card p-8 sm:p-10">
        <span className="inline-flex size-11 items-center justify-center rounded-full bg-forest text-primary-foreground">
          <Check aria-hidden="true" className="size-5" />
        </span>
        <h2 ref={headingRef} tabIndex={-1} className="display-md mt-6 outline-none">
          {result.status === "not_configured"
            ? "One last step to send your request."
            : "Your idea is on its way to Paul."}
        </h2>
        <p className="lede mt-4">
          {result.status === "not_configured"
            ? "Your details are ready to go — send the pre-filled email below and Paul will take it from there."
            : "Paul will review the details and contact you to discuss what is possible, confirm timing and provide pricing before sending a PayPal invoice. Submitting this form does not place an order or start production."}
        </p>
        {result.status === "not_configured" && (
          <div className="mt-8 border-t border-hairline pt-6">
            <p className="text-sm font-semibold">One last step</p>
            <p className="mt-2 text-sm text-muted-foreground">
              We could not deliver your request automatically just now, so nothing has been sent silently. Open the
              pre-filled email below to send your request to Paul, or copy the summary and email it yourself to{" "}
              {site.email}.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a
                href={result.mailto}
                className="tap-safe inline-flex items-center justify-center rounded-sm bg-forest px-6 text-sm font-semibold text-primary-foreground"
              >
                Open pre-filled email to Paul
              </a>
              <button
                type="button"
                onClick={async () => {
                  await navigator.clipboard.writeText(result.summary);
                  setCopied(true);
                }}
                className="tap-safe inline-flex items-center justify-center rounded-sm border border-ink/25 px-6 text-sm font-semibold hover:bg-paper"
              >
                {copied ? "Summary copied" : "Copy request summary"}
              </button>
            </div>
            <pre className="mt-6 max-h-64 overflow-auto whitespace-pre-wrap border border-hairline bg-paper p-4 text-xs leading-relaxed">
              {result.summary}
            </pre>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="surface-card overflow-hidden">
      <div className="border-b border-hairline bg-paper px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-semibold">
            Step {step + 1} of {total}
          </p>
          <p className="text-xs text-muted-foreground">{stepTitles[step]}</p>
        </div>
        <div
          className="mt-3 h-1 w-full overflow-hidden rounded-full bg-hairline"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={step + 1}
          aria-label="Request progress"
        >
          <div
            className="h-full bg-brass motion-craft transition-[width]"
            style={{ width: `${((step + 1) / total) * 100}%` }}
          />
        </div>
      </div>

      <form
        className="p-6 sm:p-8"
        onSubmit={(e) => {
          e.preventDefault();
          step === total - 1 ? onSubmit() : goNext();
        }}
        noValidate
      >
        <h2 ref={headingRef} tabIndex={-1} className="display-md outline-none">
          {stepTitles[step]}
        </h2>

        {Object.keys(errors).length > 0 && (
          <div
            role="alert"
            className="mt-5 border-l-2 border-destructive bg-destructive/5 p-4 text-sm text-destructive"
          >
            <p className="font-semibold">Please check the following:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {Object.entries(errors).map(([k, v]) => (
                <li key={k}>{v}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6">
          {step === 0 && <Choices options={wizardOptions.interest} value={data.interest} onSelect={(v) => set("interest", v)} name="Interest" />}
          {step === 1 && <Choices options={wizardOptions.recipient} value={data.recipient} onSelect={(v) => set("recipient", v)} name="Recipient" />}
          {step === 2 && <Choices options={wizardOptions.occasions} value={data.occasion} onSelect={(v) => set("occasion", v)} name="Occasion" />}
          {step === 3 && <Choices options={wizardOptions.styles} value={data.style} onSelect={(v) => set("style", v)} name="Style" />}
          {step === 4 && (
            <MultiChoices
              options={wizardOptions.materials}
              values={data.materials}
              onToggle={(v) => toggleMulti("materials", v)}
              name="Materials"
              hint="Choose as many as you like, or ask for Paul's recommendation."
            />
          )}
          {step === 5 && (
            <div className="space-y-6">
              <MultiChoices
                options={wizardOptions.personalization}
                values={data.personalization}
                onToggle={(v) => toggleMulti("personalization", v)}
                name="Personalization"
              />
              <Field label="Exact wording, names or dates (optional)" id="personalizationDetail">
                <textarea
                  id="personalizationDetail"
                  rows={3}
                  maxLength={400}
                  value={data.personalizationDetail}
                  onChange={(e) => set("personalizationDetail", e.target.value)}
                  className={inputCls}
                />
              </Field>
            </div>
          )}
          {step === 6 && (
            <div className="grid gap-6 sm:grid-cols-2">
              <Field label="Desired completion date (optional)" id="completionDate">
                <input
                  id="completionDate"
                  type="date"
                  value={data.completionDate}
                  onChange={(e) => set("completionDate", e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Quantity" id="quantity">
                <input
                  id="quantity"
                  type="number"
                  min={1}
                  max={500}
                  value={data.quantity}
                  onChange={(e) => set("quantity", e.target.value)}
                  className={inputCls}
                />
              </Field>
              <div className="sm:col-span-2">
                <Choices options={wizardOptions.budgets} value={data.budget} onSelect={(v) => set("budget", v)} name="Budget range" />
              </div>
            </div>
          )}
          {step === 7 && (
            <FileStep
              files={data.inspirationFiles}
              onChange={(names) => set("inspirationFiles", names)}
              onError={(m) => setErrors(m ? { files: m } : {})}
            />
          )}
          {step === 8 && (
            <div className="grid gap-6 sm:grid-cols-2">
              <Field label="First name" id="firstName" required error={errors.firstName}>
                <input id="firstName" autoComplete="given-name" value={data.firstName} onChange={(e) => set("firstName", e.target.value)} className={inputCls} required />
              </Field>
              <Field label="Last name" id="lastName" required error={errors.lastName}>
                <input id="lastName" autoComplete="family-name" value={data.lastName} onChange={(e) => set("lastName", e.target.value)} className={inputCls} required />
              </Field>
              <Field label="Email" id="email" required error={errors.email}>
                <input id="email" type="email" autoComplete="email" value={data.email} onChange={(e) => set("email", e.target.value)} className={inputCls} required />
              </Field>
              <Field label="Telephone (optional)" id="phone">
                <input id="phone" type="tel" autoComplete="tel" value={data.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
              </Field>
              <div className="sm:col-span-2">
                <Choices options={wizardOptions.contactMethods} value={data.contactMethod} onSelect={(v) => set("contactMethod", v)} name="Preferred contact method" />
              </div>
              <div className="sm:col-span-2">
                <Field label="Anything else Paul should know? (optional)" id="notes" error={errors.notes}>
                  <textarea id="notes" rows={4} maxLength={1200} value={data.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} />
                </Field>
              </div>
            </div>
          )}
          {step === 9 && (
            <div>
              <p className="text-sm text-muted-foreground">
                Check the details below. Use Edit on any line to go back — nothing you have entered is lost.
              </p>
              <dl className="mt-6 divide-y divide-hairline border-y border-hairline text-sm">
                {reviewRows(data).map((row) => (
                  <div key={row.label} className="grid grid-cols-[1fr_auto] items-start gap-4 py-3 sm:grid-cols-[12rem_1fr_auto]">
                    <dt className="font-semibold">{row.label}</dt>
                    <dd className="col-start-1 row-start-2 text-muted-foreground sm:col-start-2 sm:row-start-1">
                      {row.value || "—"}
                    </dd>
                    <button
                      type="button"
                      onClick={() => {
                        setStep(row.step);
                        focusHeading();
                      }}
                      className="col-start-2 row-start-1 text-xs font-semibold text-walnut underline underline-offset-4 sm:col-start-3"
                    >
                      Edit<span className="sr-only"> {row.label}</span>
                    </button>
                  </div>
                ))}
              </dl>

              <label className="mt-6 flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={privacyOk}
                  onChange={(e) => setPrivacyOk(e.target.checked)}
                  className="mt-1 size-4 accent-[var(--color-forest)]"
                />
                <span className="text-muted-foreground">
                  I understand Paul will use these details only to reply about this request, as described in the{" "}
                  <a href="/privacy" className="text-ink underline underline-offset-4">
                    privacy policy
                  </a>
                  .
                </span>
              </label>

              {result?.status === "error" && (
                <div role="alert" className="mt-5 border-l-2 border-destructive bg-destructive/5 p-4 text-sm text-destructive">
                  <p className="font-semibold">That did not go through.</p>
                  <p className="mt-1">{result.message} Please try again, or email {site.email} directly.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Honeypot — hidden from users and assistive tech */}
        <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
          <label htmlFor="company-website">Do not fill this in</label>
          <input ref={honeypot} id="company-website" name="company-website" type="text" tabIndex={-1} autoComplete="off" />
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-hairline pt-6">
          {step > 0 && (
            <button
              type="button"
              onClick={goBack}
              className="tap-safe inline-flex items-center gap-1.5 rounded-sm border border-ink/25 px-5 text-sm font-semibold hover:bg-paper motion-craft transition-colors"
            >
              <ChevronLeft aria-hidden="true" className="size-4" /> Back
            </button>
          )}
          <CtaButton type="submit" arrow={step < total - 1} disabled={submitting} className="h-12 disabled:opacity-70">
            {submitting && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
            {step === total - 1 ? (submitting ? "Sending…" : "Send my request to Paul") : "Continue"}
          </CtaButton>
          <p className="text-xs text-muted-foreground">Sending this does not place an order.</p>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  "w-full rounded-sm border border-input bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-brass";

function validate(step: number, data: CustomRequest): Record<string, string> {
  const e: Record<string, string> = {};
  if (step === 0 && !data.interest) e.interest = "Please choose what you are interested in.";
  if (step === 1 && !data.recipient) e.recipient = "Please tell Paul who the piece is for.";
  if (step === 2 && !data.occasion) e.occasion = "Please choose an occasion, or select Other.";
  if (step === 3 && !data.style) e.style = "Please choose a style, or select Not sure.";
  if (step === 4 && !data.materials.length) e.materials = "Please choose at least one material option.";
  if (step === 6 && !data.budget) e.budget = "Please choose an approximate budget range.";
  if (step === 8) {
    const parsed = contactSchema.safeParse(data);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) e[String(issue.path[0])] = issue.message;
    }
  }
  return e;
}

function reviewRows(d: CustomRequest) {
  return [
    { label: "Interested in", value: d.interest, step: 0 },
    { label: "For", value: d.recipient, step: 1 },
    { label: "Occasion", value: d.occasion, step: 2 },
    { label: "Style", value: d.style, step: 3 },
    { label: "Materials", value: d.materials.join(", "), step: 4 },
    {
      label: "Personalization",
      value: [d.personalization.join(", "), d.personalizationDetail].filter(Boolean).join(" — "),
      step: 5,
    },
    { label: "Timing", value: d.completionDate || "Flexible", step: 6 },
    { label: "Budget", value: d.budget, step: 6 },
    { label: "Quantity", value: d.quantity, step: 6 },
    { label: "Inspiration", value: d.inspirationFiles.join(", "), step: 7 },
    { label: "Name", value: `${d.firstName} ${d.lastName}`.trim(), step: 8 },
    { label: "Email", value: d.email, step: 8 },
    { label: "Telephone", value: d.phone, step: 8 },
    { label: "Preferred contact", value: d.contactMethod, step: 8 },
    { label: "Notes", value: d.notes, step: 8 },
  ];
}

function Choices({
  options,
  value,
  onSelect,
  name,
}: {
  options: readonly string[];
  value: string;
  onSelect: (v: string) => void;
  name: string;
}) {
  return (
    <fieldset>
      <legend className="sr-only">{name}</legend>
      <div className="flex flex-wrap gap-2.5">
        {options.map((o) => (
          <label
            key={o}
            className={cn(
              "tap-safe inline-flex cursor-pointer items-center rounded-sm border px-4 text-sm font-medium motion-craft transition-colors",
              value === o ? "border-forest bg-forest text-primary-foreground" : "border-ink/20 hover:border-ink hover:bg-paper",
            )}
          >
            <input
              type="radio"
              name={name}
              value={o}
              checked={value === o}
              onChange={() => onSelect(o)}
              className="sr-only"
            />
            {o}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function MultiChoices({
  options,
  values,
  onToggle,
  name,
  hint,
}: {
  options: readonly string[];
  values: string[];
  onToggle: (v: string) => void;
  name: string;
  hint?: string;
}) {
  return (
    <fieldset>
      <legend className="sr-only">{name}</legend>
      {hint && <p className="mb-4 text-sm text-muted-foreground">{hint}</p>}
      <div className="flex flex-wrap gap-2.5">
        {options.map((o) => {
          const on = values.includes(o);
          return (
            <label
              key={o}
              className={cn(
                "tap-safe inline-flex cursor-pointer items-center gap-2 rounded-sm border px-4 text-sm font-medium motion-craft transition-colors",
                on ? "border-forest bg-forest text-primary-foreground" : "border-ink/20 hover:border-ink hover:bg-paper",
              )}
            >
              <input type="checkbox" checked={on} onChange={() => onToggle(o)} className="sr-only" />
              {on && <Check aria-hidden="true" className="size-3.5" />}
              {o}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function Field({
  label,
  id,
  children,
  required,
  error,
}: {
  label: string;
  id: string;
  children: React.ReactNode;
  required?: boolean;
  error?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold">
        {label}
        {required && <span className="text-walnut"> *</span>}
      </label>
      <div className="mt-2">{children}</div>
      {error && (
        <p className="mt-1.5 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function FileStep({
  files,
  onChange,
  onError,
}: {
  files: string[];
  onChange: (names: string[]) => void;
  onError: (message: string | null) => void;
}) {
  const handle = (list: FileList | null) => {
    if (!list) return;
    const picked = Array.from(list);
    const bad = picked.find(
      (f) => !UPLOAD_LIMITS.accept.includes(f.type) || f.size > UPLOAD_LIMITS.maxSizeMb * 1024 * 1024,
    );
    if (bad) {
      onError(`${bad.name} could not be attached. Use ${UPLOAD_LIMITS.acceptLabel} under ${UPLOAD_LIMITS.maxSizeMb} MB.`);
      return;
    }
    const names = [...files, ...picked.map((f) => f.name)].slice(0, UPLOAD_LIMITS.maxFiles);
    onError(null);
    onChange(names);
    track("upload_inspiration", { count: names.length });
  };

  return (
    <div>
      <p className="text-sm text-muted-foreground">
        A photo, a sketch or a screenshot helps more than a paragraph. Optional — skip this step if you would
        rather describe it in words.
      </p>
      <label className="mt-5 flex cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-input bg-paper/50 px-6 py-10 text-center motion-craft transition-colors hover:border-brass">
        <Paperclip aria-hidden="true" className="size-5 text-walnut" />
        <span className="text-sm font-semibold">Choose files</span>
        <span className="text-xs text-muted-foreground">
          {UPLOAD_LIMITS.acceptLabel} · up to {UPLOAD_LIMITS.maxFiles} files · {UPLOAD_LIMITS.maxSizeMb} MB each
        </span>
        <input
          type="file"
          multiple
          accept={UPLOAD_LIMITS.accept.join(",")}
          className="sr-only"
          onChange={(e) => handle(e.target.files)}
        />
      </label>
      {files.length > 0 && (
        <ul className="mt-4 space-y-2">
          {files.map((f) => (
            <li key={f} className="flex items-center justify-between gap-3 border border-hairline px-3 py-2 text-sm">
              <span className="truncate">{f}</span>
              <button
                type="button"
                onClick={() => onChange(files.filter((x) => x !== f))}
                className="tap-safe inline-flex items-center justify-center text-muted-foreground hover:text-ink"
                aria-label={`Remove ${f}`}
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-xs text-muted-foreground">
        File names are included with your request. Attach the files themselves to the email Paul receives, or
        send them separately — secure private storage will be connected before uploads are stored on the site.
      </p>
    </div>
  );
}
